// src/Header.jsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { signInWithRedirect, signOut as authSignOut, fetchAuthSession } from 'aws-amplify/auth';
import { useSession } from './auth/SessionProvider.jsx';
import Logo from './images/AFPToolBox.png';

export default function Header() {
  const { initializing, signedIn } = useSession(); // <- from your SessionProvider
  const [displayName, setDisplayName] = React.useState(null);
  const menuRef = React.useRef(null);
  const location = useLocation();

  const closeMenu = React.useCallback(() => {
    if (menuRef.current) menuRef.current.open = false;
  }, []);

  // Update display name when auth state changes
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!signedIn) { if (alive) setDisplayName(null); return; }
      try {
        const { tokens } = await fetchAuthSession();
        const idp = tokens?.idToken?.payload ?? {};
        const full =
          idp.name ||
          [idp.given_name, idp.family_name].filter(Boolean).join(' ') ||
          idp.email ||
          null;
        if (alive) setDisplayName(full);
      } catch {
        if (alive) setDisplayName(null);
      }
    })();
    return () => { alive = false; };
  }, [signedIn]);

  // Close dropdown on route change
  React.useEffect(() => { closeMenu(); }, [location.pathname, closeMenu]);

  if (initializing) return null;

  const handleAuthClick = async () => {
    try {
      if (signedIn) {
        await authSignOut({ global: true });
        // Ensure UI reflects sign-out immediately
        setDisplayName(null);
        // If SessionProvider doesn’t emit Hub events, force a soft reload
        window.location.assign('/');
      } else {
        await signInWithRedirect(); // Hosted UI
      }
    } catch (e) {
      // Optional: surface a toast/log; don’t block UI
      console.error('Auth action failed:', e);
    }
  };

  return (
    <header className="site-header">
      <div className="header-row">
        {/* LEFT: Logo */}
        <div className="navdiv">
          <Link to="/" className="brand" aria-label="AFPToolBox Home">
            <img className="nav-logo" src={Logo} alt="AFPToolBox logo" />
          </Link>
        </div>

        {/* CENTER: Title */}
        <div className="navdivcenter">
          <h1 className="main-title">AFPToolBox</h1>
        </div>

        {/* RIGHT: Auth + dropdown */}
        <div className="navdiv" style={{ textAlign: 'right', position: 'relative' }}>
          <button className="link-btn" onClick={handleAuthClick}>
            {signedIn ? `Sign out${displayName ? ` (${displayName})` : ''}` : 'Sign in'}
          </button>
          <span className="divider" aria-hidden="true"> | </span>
          <details ref={menuRef} className="nav-link-dropdown">
            <summary className="link-btn">Menu ▾</summary>
            <div className="nav-dropdown-menu">
              <nav aria-label="Menu">
                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
                  <Link className="nav-link" to="/" onClick={closeMenu}>Home</Link>
                  <Link className="nav-link" to="/performance" onClick={closeMenu}>Performance</Link>
                  <Link className="nav-link" to="/detailing" onClick={closeMenu}>Detailing</Link>
                  <Link className="nav-link" to="/admin" onClick={closeMenu}>Admin</Link>
                </div>
              </nav>
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}
